import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActionSheetKeyboardAwareView } from './action-sheet-keyboard-aware-view';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { DeleteCollectionConfirmation } from './delete-collection-confirmation';
import { EditOrderGuideActions } from '@/hooks/use-edit-order-guide-reducer';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import {
  useEditOrderGuideActions,
  useEditOrderGuideState,
} from '@/contexts/edit-order-guide-context';
import { validateCollectionName } from '@/utils/order-guide';

interface Props {
  collectionNames: string[];
  deleteCollectionMode: 'delete' | 'reset' | null;
  initialName: string;
  onClose: () => void;
  onDelete: () => void;
}

enum EditCollectionSheetRoute {
  Delete = 'DELETE',
  Edit = 'EDIT',
}

export const EditCollectionActionSheet: React.FC<Props> = ({
  collectionNames,
  deleteCollectionMode,
  initialName,
  onClose,
  onDelete,
}) => {
  const dispatch = useEditOrderGuideActions();
  const { collectionUUID } = useEditOrderGuideState();
  const [inputError, setInputError] = useState('');

  return (
    <CombinedActionSheet
      initialRoute={EditCollectionSheetRoute.Edit}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case EditCollectionSheetRoute.Delete:
            if (!collectionUUID || !deleteCollectionMode) {
              return null;
            }
            return (
              <DeleteCollectionConfirmation
                collectionName={initialName}
                collectionUUID={collectionUUID}
                deleteCollectionMode={deleteCollectionMode}
                onCancel={() => jumpTo(EditCollectionSheetRoute.Edit)}
                onDelete={onDelete}
              />
            );
          case EditCollectionSheetRoute.Edit:
            return (
              <ActionSheetKeyboardAwareView>
                <OrderGuideActionSheetNameInput
                  deleteButtonProps={
                    deleteCollectionMode
                      ? {
                          children:
                            deleteCollectionMode === 'reset'
                              ? i18n.t('reset_order_guide')
                              : i18n.t('delete_order_guide'),
                          onPress: () => {
                            jumpTo(EditCollectionSheetRoute.Delete);
                          },
                          trackingData: {
                            name: 'edit_collection_sheet.delete_button',
                            properties: {
                              collectionName: initialName,
                              collectionUUID,
                            },
                          },
                        }
                      : undefined
                  }
                  initialName={initialName}
                  inputError={inputError}
                  nameInputLabel={i18n.t('order_guide_name')}
                  onChangeText={() => setInputError('')}
                  onSave={(name) => {
                    if (!validateCollectionName(name, collectionNames)) {
                      setInputError(i18n.t('order_guide_name_error'));
                    } else {
                      dispatch({
                        payload: {
                          collectionName: name,
                        },
                        type: EditOrderGuideActions.SET_COLLECTION_NAME,
                      });
                      onClose();
                    }
                  }}
                />
              </ActionSheetKeyboardAwareView>
            );
          default:
            return null;
        }
      }}
      routes={{
        [EditCollectionSheetRoute.Delete]: {
          previousKey: EditCollectionSheetRoute.Edit,
          showSmallLayoutBackButton: false,
          showSmallLayoutTitle: false,
        },
        [EditCollectionSheetRoute.Edit]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('edit_order_guide_name'),
        },
      }}
    />
  );
};
